const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/utils/labReportUtils.ts';
import Imm from 'immutable';
import { Theme } from '@mui/material';
import { Logger } from '../services/KisLoggerService';
import * as LabDefs from '../defs/labReportDefs';
import { colors } from '../defs/colors';
import { strContainsSome, strContainsUppercaseWordLength } from './stringUtils';
import { LoadCumulDataMeta, LoadCumulSummaryMeta } from '../actions/labReportsActions';
import { getMainConfig } from '../services/mainConfig';
import { LabReportColumn } from '../selectors/labReportsSelectors';

const Log = Logger('labReportUtils');
const emptyList = Imm.List<any>();
const emptyMap = Imm.Map<string, any>();

/**
 * Determines whether a row is visible in the current presentation mode.
 * @param defs entry from rowDefs
 * @param presentationMode current presentation mode
 */
export function isLabResultVisible(defs: Imm.Map<string, any>, presentationMode: string) {
    const labId = defs.get('LabId');

    if (!labId) {
        return false; // rows with missing LabIds are hidden
    }

    if (presentationMode === LabDefs.CHART_MODE) {
        const refList = defs.get('refList');
        const hasNumericalRefRange = (refList || Imm.List<any>()).reduce(
            (result: boolean, entry: Imm.Map<string, any>) => {
                return result || typeof entry.get('min') === 'number' || typeof entry.get('max') === 'number';
            },
            false
        );

        if (!hasNumericalRefRange && !defs.has('minVal') && !defs.has('maxVal')) {
            return false; // this does not appear to be a numerical row
        }

        // NOTE: Most labIds are hospital-specific! Should we ever encounter any collisions between
        // hospitals, the following list will have to be moved into the config!
        const labIdsExcludedFromCharts = ['COMMENT', 'REM_ORD', 'ANALYSATOR', 'MATERIAL'];

        if (labIdsExcludedFromCharts.indexOf(labId) >= 0) {
            return false;
        }
    }

    return true;
}

/**
 * Determines whether a cell is visible. Note that you should also call isLabResultVisible to check if the cell's row is
 * visible.
 * @param cell cell data
 * @param requiredFlags if defined and non-empty, a cell must have at least one of the required flags to be visible
 * @return true if the cell is visible, false if not.
 */
export function isLabCellVisible(
    cell: Imm.Map<string, any> | undefined,
    requiredFlags: Imm.List<string> | undefined
): boolean {
    if (!cell) {
        return false;
    }

    if (requiredFlags && requiredFlags.size > 0) {
        const flag = cell.get('FLAG');

        if (!flag || requiredFlags.indexOf(flag) < 0) {
            return false;
        }
    }

    return !!cell.get('RES') || !!cell.get('CommentId') || !!cell.get('resCommentId');
}

/**
 * Attempts to estimate the importance of a given text. This is used by
 * LabReportsBoard to colourize the comment icons in a hopefully meaningful
 * way.
 * @return: One of LabDefs.*_IMPORTANCE
 */
export function estimateImportance(text: string) {
    const lowerText = text.toLowerCase();

    // High importance

    if (strContainsSome(lowerText, ['!!', 'wichtig', 'achtung', 'warnung', 'unbedingt', 'unverzüglich'])) {
        return LabDefs.HIGH_IMPORTANCE;
    }

    if (strContainsUppercaseWordLength(text) >= 4) {
        return LabDefs.HIGH_IMPORTANCE;
    }

    // Medium importance

    if (text.length > 50) {
        return LabDefs.MEDIUM_IMPORTANCE;
    }

    if (
        strContainsSome(lowerText, [
            '!',
            'keine',
            'ungültig',
            'annulliert',
            'pathologisch',
            'fehler',
            'fraglich',
            'vorbehalt',
            'störung',
            'instabil',
            'beachten',
            'unmöglich',
            'verdacht',
        ])
    ) {
        return LabDefs.MEDIUM_IMPORTANCE;
    }

    // Low importance

    return LabDefs.LOW_IMPORTANCE;
}

/**
 * Converts an importance value to a colour value.
 */
export function importanceToColor(importance: string, theme: Theme): string {
    switch (importance) {
        case LabDefs.HIGH_IMPORTANCE:
            return theme.palette.error.main;
        case LabDefs.MEDIUM_IMPORTANCE:
            return theme.palette.warning.main;
        default:
            return colors.grey['400'];
    }
}

/**
 * Generates a key that doesn't exist as a top-level key in the map forMap yet.
 * @param args
 *    forMap (in): The map in which the key is supposed to be unique.
 *    chars (in): Characters allowed to be used as a key.
 *    sourceText (in): If defined, chars occuring in this string will be preferred.
 *    allowCharsNotInSource (in): If true, characters occurring in chars but not
 *       occurring in sourceText are allowed. If not specified, the flag defaults to true
 *       if sourceText is undefined, and to false if sourceText is defined.
 *    allowNumerical (in): If true, the method will fall back to a numerical
 *       key if no unique key could be determined otherwise.
 * @return: A string, which is guaranteed not to exist in forMap yet.
 */
export const generateKey = (args: {
    forMap: Imm.Map<string, any>;
    chars: string;
    sourceText?: string;
    allowCharsNotInSource?: boolean;
    allowNumerical?: boolean;
}): string => {
    // Prefer characters occurring in sourceText.

    let result: string | undefined = undefined;

    if (args.sourceText) {
        args.sourceText.split('').forEach((c: string) => {
            if (!result) {
                if (args.chars.indexOf(c) >= 0) {
                    if (!args.forMap.has(c)) {
                        result = c;
                        // can't break
                    }
                }
            }
        });
    }

    if (result) {
        return result;
    }

    // Examine characters not occurring in sourceText, if allowed.

    if (
        args.allowCharsNotInSource ||
        (typeof args.sourceText === 'undefined' && typeof args.allowCharsNotInSource === 'undefined')
    ) {
        args.chars.split('').forEach((c: string) => {
            if (!result) {
                if (!args.forMap.has(c)) {
                    result = c;
                    // can't break
                }
            }
        });
    }

    if (result) {
        return result;
    }

    // Attempt to find a numerical key, if allowed.

    if (args.allowNumerical) {
        for (let i = 1; ; i++) {
            const c = `${i}`;

            if (!args.forMap.has(c)) {
                result = c;
                break;
            }
        }
    }

    if (result) {
        return result;
    }

    // We're out of luck.

    throw new Error('Failed to find a unique new key!');
};

/**
 * Parses the range indicated by the given string. The following formats are recognized:
 *    m-n     (from m to n when both m and n are positive)
 *    -m - n  (from -m to n)
 *    m - -n  (from m to -n)
 *    -m - -n (from -m to -n)
 *    m...n   (from m to n, both m and n may be negative)
 *    m..n    (from m to n, both m and n may be negative)
 *    >m      (m is lower bound)
 *    <n      (n is upper bound)
 *    >=m     (m is lower bound and included in the range)
 *    <=n     (n is upper bound and included in the range)
 *    m       (m is a point of reference resulting in an empty range)
 *
 * The following formats are currently NOT recognized:
 *    -m-n  (from -m to n, missing whitespace)
 *    m--n  (from m to -n, missing whitespace)
 *    -m--n (from -m to -n, missing whitespace)
 *
 * @return: A range, or null if the range was not recognized.
 */
export function parseRange(strInput: string): LabDefs.IRange | null {
    if (!strInput) {
        return null;
    }

    strInput = strInput.trim();

    if (strInput) {
        // Trying formats >m and >=m

        if (strInput.charAt(0) === '>') {
            let startIdx = 1;

            if (strInput.charAt(1) === '=') {
                startIdx++;
            }

            const sm = strInput.slice(startIdx);

            if (sm) {
                const m = Number(sm);

                if (!Number.isNaN(m)) {
                    return { min: m };
                }
            }
        }

        // Trying formats <n and <=n

        if (strInput.charAt(0) === '<') {
            let startIdx = 1;

            if (strInput.charAt(1) === '=') {
                startIdx++;
            }

            const sn = strInput.slice(startIdx);

            if (sn) {
                const n = Number(sn);

                if (!Number.isNaN(n)) {
                    return { max: n };
                }
            }
        }

        // Trying formats m-n, m...n, m..n

        const delims = [' - ', '-', '...', '..'];

        for (const delim of delims) {
            const parts = strInput.split(delim);

            if (parts.length === 2) {
                const sm = parts[0].trim();
                const sn = parts[1].trim();

                if (sm && sn) {
                    const m = Number(sm);
                    const n = Number(sn);

                    if (!Number.isNaN(m) && !Number.isNaN(n)) {
                        if (m > n) {
                            return { min: n, max: m };
                        } else {
                            return { min: m, max: n };
                        }
                    }
                }
            }
        }

        // Trying format m (point of reference)

        if (!Number.isNaN(Number(strInput))) {
            return {
                min: Number(strInput),
                max: Number(strInput),
            };
        }
    }

    return null;
}

/**
 * Determines the range of the values expected for a lab result of the given type. Returning the
 * correct range here will help charts to determine the y domain.
 * @param defs (in): entry from rowDefs
 * @param visibleMin (in): The minimum of the currently visible values.
 * @param visibleMax (in): The maximum of the currently visible values.
 * @param refMin
 * @param refMax
 */
export function getExpectedRangeOfLabResults(
    defs: Imm.Map<string, any>,
    visibleMin?: number,
    visibleMax?: number,
    refMin?: number,
    refMax?: number
): LabDefs.IRange {
    let expectedMin = Number.MAX_SAFE_INTEGER;
    let expectedMax = Number.MIN_SAFE_INTEGER;

    // The range should always cover the visible samples.

    if (typeof visibleMin === 'number') {
        expectedMin = Math.min(expectedMin, visibleMin);
    }

    if (typeof visibleMax === 'number') {
        expectedMax = Math.max(expectedMax, visibleMax);
    }

    // When visible samples are all the same, they should be near the centre of the domain.

    if (typeof visibleMin === 'number' && typeof visibleMax === 'number') {
        if (visibleMin >= visibleMax) {
            expectedMin = Math.min(expectedMin, Math.min(visibleMin, visibleMax) - 0.5);
            expectedMax = Math.max(expectedMax, Math.max(visibleMin, visibleMax) + 0.5);
        }
    }

    // The range should always cover the (closed) range of reference.

    if (typeof refMin === 'number') {
        expectedMin = Math.min(expectedMin, refMin);
    }

    if (typeof refMax === 'number') {
        expectedMax = Math.max(expectedMax, refMax);
    }

    // Avoid returning the guardian values.

    if (expectedMin === Number.MAX_SAFE_INTEGER || expectedMax === Number.MIN_SAFE_INTEGER) {
        expectedMin = 0;
        expectedMax = 0;
    }

    // The range should be close to the overall minimum and maximum. (We don't fully cover them in
    // the range to avoid returning a bad range when there is one outlier in data.)

    const minVal = defs.get('minVal');
    const maxVal = defs.get('maxVal');

    if (typeof minVal === 'number') {
        const closeMin = minVal * 0.9 + expectedMin * 0.1; // 10% closer to minVal
        expectedMin = Math.min(expectedMin, closeMin);
    }

    if (typeof maxVal === 'number') {
        const closeMax = maxVal * 0.9 + expectedMax * 0.1; // 10% closer to maxVal
        expectedMax = Math.max(expectedMax, closeMax);
    }

    // Include zero when the range is close to it.

    if (0 < expectedMin && expectedMin < 10) {
        expectedMin = 0;
    } else if (-10 < expectedMax && expectedMax < 0) {
        expectedMax = 0;
    }

    // Properly round the range we've found.

    if (expectedMax - expectedMin > 2) {
        // Round to full integers
        expectedMin = Math.floor(expectedMin);
        expectedMax = Math.ceil(expectedMax);
    } else {
        // Round to halves.
        expectedMin = Math.floor(expectedMin * 2) / 2;
        expectedMax = Math.ceil(expectedMax * 2) / 2;
    }

    return { min: expectedMin, max: expectedMax };
}

/**
 * Determines the canonical presentation of the given range or point of reference.
 * @return: A string
 */
export function getCanonicalRange(range: LabDefs.IRange): string {
    let values: number[];
    let delim = '';

    if (typeof range.min !== 'undefined') {
        if (typeof range.max !== 'undefined') {
            if (range.min !== range.max) {
                // A closed range.
                values = [range.min, range.max];
                delim = ' - ';
            } else {
                // An empty range, interpreted as a point of reference.
                values = [range.min];
            }
        } else {
            // An open range with a lower bound.
            values = [range.min];
            delim = '> ';
        }
    } else if (typeof range.max !== 'undefined') {
        // An open range with an upper bound.
        values = [range.max];
        delim = '< ';
    } else {
        throw new Error(`Invalid range: ${JSON.stringify(range)}`);
    }

    const info = values.map((val: number) => {
        const parts = Math.abs(val).toFixed(4).split('.');
        return {
            sgn: Math.sign(val),
            int: parts[0],
            frc: parts[1],
        };
    });

    const maxFracLen = info.reduce((res: number, entry: any) => {
        let n = entry.frc.length;

        for (; n > 0; n--) {
            if (entry.frc.charAt(n - 1) !== '0') {
                break;
            }
        }

        return Math.max(res, n);
    }, 0);

    let result = values.length === 1 ? delim : '';

    info.forEach((entry: any, idx: number) => {
        if (entry.sgn < 0) {
            result += '\u2013'; // ndash
        }

        result += entry.int;

        if (maxFracLen > 0) {
            result += '.' + entry.frc.slice(0, maxFracLen);
        }

        if (idx === 0 && values.length > 1) {
            result += delim;
        }
    });

    return result;
}

/**
 * This method helps work around the problem that the units member of a rowDefs entry may come with
 * slightly different spellings among Einzelresultate. The method determines the canonical form of
 * the spelling. It also zaps certain units by returning null when they aren't considered to be
 * units as such. (We do this to be able to show the unit along with the value at all times, and
 * avoid nonsensical combinations such as '0.5 fraction'.)
 * @return: A string, or null if the units member is to be zapped
 */
export function getCanonicalUnits(units: string): string | null {
    if (!units) {
        return null;
    }

    switch (units) {
        case '-':
            return null; // maintain a consistent way of telling 'no units specified'
        case '+':
            return null; // not a unit, zap it!
        case 'fraction':
            return null; // not a unit, zap it!
        case '1':
            return null; // not a unit, zap it!
        case '/100Lc':
            return '/100 Lc';
        case 'micromol':
            return 'µmol';
        case 'micromol/l':
            return 'µmol/l';
    }

    return units;
}

/**
 * Determines the Ref range to be displayed
 */
export function determineDisplayedRefRange(rowDefs: Imm.Map<string, any>, visibleColumns: LabReportColumn[]) {
    const refList = rowDefs.get('refList') || Imm.List<any>();
    const refMap = rowDefs.get('refMap') || Imm.Map<string, any>();

    let ref: Imm.Map<string, any> | null = null;
    let prevRef: Imm.Map<string, any> | null = null;
    let refDate = '';
    let prevRefDate = '';

    visibleColumns
        .map(col => col.reportDat)
        .sort()
        .forEach((date: string) => {
            const idx = !!date && refMap.get(date);
            const entry = typeof idx === 'number' && refList.get(idx);

            if (entry) {
                if (entry !== ref && ref) {
                    prevRef = ref;
                    prevRefDate = refDate;
                }

                ref = entry;
                refDate = date;
            }
        });

    return { ref, prevRef, refDate, prevRefDate };
}

/**
 * Counts the number of avail items below a given date.
 */
export function countAvailItemsBelow(maxdat: string, availList: Imm.List<any>): number {
    return (availList || emptyList).reduce((result: number, availItem: Imm.Map<string, any>) => {
        const dat = availItem.get('Date');
        return result + (dat < maxdat ? 1 : 0);
    }, 0);
}

/**
 * Helper function to add some details to the action meta.
 */
export function addDetailsToActionMeta(
    meta: LoadCumulSummaryMeta | LoadCumulDataMeta,
    cumulDefs: Imm.Map<string, any>,
    fetchOptions: Imm.Map<string, any>
): void {
    meta.filterIcode = cumulDefs.get('FilterIcode');
    meta.mapIcode = fetchOptions?.get('mapIcode');

    if (meta.filterIcode) {
        meta.dataSource = 'cumul-report-v2';
    } else if (meta.mapIcode) {
        meta.dataSource = 'report-map';
    } else {
        Log.warn('addDetailsToActionMeta: Meta is missing mandatory data!', meta);
        meta.dataSource = 'cumul-report-v2';
    }

    meta.configIcode = getMainConfig().settings.labCumulConfigIcode;
}

/**
 * Finds the index of the avail item with the given SENDER and REPORTNR.
 * @param report the entry from cumulReports
 * @param sender
 * @param reportnr
 * @return An index into availList, or -1 if the avail item was not found.
 */
export function findIdxOfAvailItem(report: Imm.Map<string, any>, sender: string, reportnr: string): number {
    const availList = report?.get('availList') || emptyList;
    return availList.reduce((result: number, availItem: Imm.Map<string, any>, idx: number) => {
        if (result < 0) {
            const s = availItem.get('SENDER');
            const r = availItem.get('REPORTNR');

            if (sender === s && reportnr === r) {
                result = idx;
            }
        }
        return result;
    }, -1);
}

/**
 * Processes the raw ref range we received from KISAPI, determining the mathematical range and the canonical form, and
 * adding it to the rowDef's refList and refMap.
 * @param rawRef the ref range we received from KISAPI
 * @param rowDefs a Map containing the currently known row definitions; key = rowId
 * @param rowId the id of the rowDefs we're processing
 * @param availItem the corresponding entry from availList
 * @return the modified rowDefs with the updated refList and refMap
 */
export function processRefRange(
    rawRef: string,
    rowDefs: Imm.Map<string, any>,
    rowId: string,
    availItem: Imm.Map<string, any>
): Imm.Map<string, any> {
    // Parse the raw ref range and bring it into canonical form.

    const parsedRef = parseRange(rawRef);
    const canonRef = parsedRef && getCanonicalRange(parsedRef);

    // Look for an identical Ref range.

    let refList = rowDefs.getIn([rowId, 'refList']) || emptyList;
    let foundAt: number;

    if (canonRef) {
        foundAt = refList.findKey((ref: Imm.Map<string, any>) => ref.get('canon') === canonRef);
    } else {
        foundAt = refList.findKey((ref: Imm.Map<string, any>) => ref.get('raw') === rawRef);
    }

    // Create a new refList entry if the Ref range wasn't found.

    if (typeof foundAt === 'undefined') {
        const newRef = {
            raw: rawRef,
            canon: canonRef ? canonRef : undefined,
            min: parsedRef && typeof parsedRef.min !== 'undefined' ? parsedRef.min : undefined,
            max: parsedRef && typeof parsedRef.max !== 'undefined' ? parsedRef.max : undefined,
        };
        const newRefImm = Imm.fromJS(newRef).filter((val: any) => typeof val !== 'undefined');
        refList = refList.push(newRefImm);
        rowDefs = rowDefs.setIn([rowId, 'refList'], refList);
        foundAt = refList.size - 1;
    }

    // Create a new refMap entry to refer to the refList entry.

    const dat = availItem.get('Date') || '?';
    const refMap = rowDefs.getIn([rowId, 'refMap']) || emptyMap;
    return rowDefs.setIn([rowId, 'refMap'], refMap.set(dat, foundAt));
}

/**
 * Processes the cells of the given map:
 *   - empty cells will be discarded
 *   - values of null and '' will be deleted
 *   - values of types other than string will be converted to string
 *   - all values will be trimmed
 *   - a member 'val' will be added when RES is a numerical value
 *   - numerical values of the form m,n will be converted to m.n
 *   - non-numerical values exceeding the max allowed length will be moved to comments
 *   - numerical values whose text representation exceed the maximum allowed length will
 *     be truncated or moved to comments
 *   - add leading zeroes to numerical values of the form .42 and -.42
 *   - dashes appearing at the start or end of RES will be replaced with ndash
 *   - members whose names collide with those reserved for internal use will be deleted
 * @param cellMap a Map from cellId to cell data
 * @param addResComment callback to be called when cell data is to be moved to comments
 * @return the modified cellMap
 */
export function processCellData(
    cellMap: Imm.Map<string, any>,
    addResComment: (res: string) => string
): Imm.Map<string, any> {
    cellMap.forEach((cell: Imm.Map<string, any>, cellId: string) => {
        // Delete reserved members.

        cell.delete('val');
        cell.delete('resCommentId');

        if (cell.has('RES')) {
            // KISAPI is expected to send back strings even for numerical values.

            let res = cell.get('RES');

            if (typeof res !== 'string') {
                Log.warn(`Cell ${cellId} having unexpected type ${typeof res} will be converted!`);
                res = res === null || typeof res === 'undefined' ? '' : `${res}`;
            }

            res = res.trim();

            if (!res) {
                // Consistently represent an empty cell with a missing RES value.
                cell = cell.delete('RES');
            } else {
                // Now that res is known to be of type string and non-empty, determine whether it represents a number.
                // We use Number instead of parseFloat in order not to misinterpret '1a' as a number. This also means
                // that 1e3, 0xff and 0o10 will be valid numbers.

                let val = Number(res);
                let isNumerical = !Number.isNaN(val);

                if (!isNumerical) {
                    // Try again with comma instead of period. (UKBAL reports appear like this.)

                    const tmpRes = res.replace(',', '.');
                    const tmpVal = Number(tmpRes);
                    isNumerical = !Number.isNaN(tmpVal);

                    if (isNumerical) {
                        res = tmpRes;
                        val = tmpVal;
                    }
                }

                // Transform various forms into a kind of canonical form.

                if (isNumerical) {
                    cell = cell.set('val', val);

                    if (res.charAt(0) === '.') {
                        // Transform numerical values of the form .42 to 0.42
                        res = `0${res}`;
                    } else if (res.slice(0, 2) === '-.') {
                        // Transform numerical values of the form -.42 to -0.42
                        res = `-0.${res.slice(2)}`;
                    }
                }

                if (res.charAt(0) === '-') {
                    // Replace leading dash with ndash.
                    res = `\u2013${res.slice(1)}`;
                } else if (res.length > 1 && res.charAt(res.length - 1) === '-') {
                    // Replace trailing dash with ndash.
                    res = `${res.slice(0, res.length - 1)}\u2013`;
                }

                cell = cell.set('RES', res);

                // Make sure the length does not exceed the limit.

                if (res.length > LabDefs.MAX_VALUE_LENGTH) {
                    // The current representation is too long. Truncate it if we can, otherwise move it to the comments!

                    let moveToComments: boolean = false;

                    if (!isNumerical) {
                        // A string that's too long is always moved to comments.
                        moveToComments = true;
                    } else {
                        let truncRes = val.toFixed(LabDefs.MIN_PRECISION_WHEN_TRUNCATING);

                        if (truncRes.length > LabDefs.MAX_VALUE_LENGTH) {
                            // The number cannot be truncated while keeping a minimum
                            // number of precision digits. Move it to comments!
                            Log.info(`Unable to truncate value (${res}), moving it to comments!`);
                            moveToComments = true;
                        } else {
                            // Ok, truncate the value!

                            const numDigits =
                                LabDefs.MIN_PRECISION_WHEN_TRUNCATING + (LabDefs.MAX_VALUE_LENGTH - truncRes.length);
                            truncRes = val.toFixed(numDigits); // truncate to fixed precision
                            truncRes = `${Number(truncRes)}`; // zap trailing zeroes

                            if (
                                truncRes.length > LabDefs.MAX_VALUE_LENGTH ||
                                Number.isNaN(Number(truncRes)) ||
                                Math.abs(Number(truncRes) - val) > 1.0
                            ) {
                                // Whoops, something went wrong!
                                Log.warn(`Failed to truncate value (${res}), moving it to comments!`);
                                moveToComments = true;
                            } else {
                                // Store the truncated value.
                                cell = cell.set('RES', truncRes);
                            }
                        }
                    }

                    if (moveToComments) {
                        const commentId = addResComment(res);
                        cell = cell.delete('RES').set('resCommentId', commentId);
                    }
                }
            }
        }

        if (cell.isEmpty()) {
            cellMap = cellMap.delete(cellId);
        } else {
            cellMap = cellMap.set(cellId, cell);
        }
    });

    return cellMap;
}

/**
 * Helper function to put a lab report's record into the state. The index is properly updated.
 */
export function putLabSingleRepIntoState(state: Imm.Map<string, any>, rec: Imm.Map<string, any>, patnr: string) {
    // Safety checks.

    if (rec.get('PATNR') !== patnr) {
        Log.error('Record with unexpected patnr: ', rec.toJS());
        return state;
    }

    const sender: string = rec.get('SENDER');
    const reportnr: string = rec.get('REPORTNR');

    if (!sender || !reportnr) {
        Log.error('Missing mandatory members from record: ', rec.toJS());
        return state;
    }

    // Put the report into the flat list of records.

    const repId = `${sender}_${reportnr}`;
    state = state.setIn(['singleReports', repId], rec);

    // Update the index singleRepByPat.

    let index = state.getIn(['index', 'singleRepByPat', patnr]) || Imm.List();

    if (
        !index.find((id: string) => {
            return id === repId;
        })
    ) {
        index = index.push(repId);
        state = state.setIn(['index', 'singleRepByPat', patnr], index);
    }

    return state;
}
